import { formatCurrency } from '@/lib/helpers/format-currency';
import type { ReferralListItem, ReferralSummary } from '@/types/referral';
import { ArrowDownRight, ArrowRightLeft, Share2 } from 'lucide-react';
import { referralAvatarColor, referralInitials } from '../utils';
import { HeroEmblem } from './hero-emblem';

interface BalanceHeroProps {
  summary: ReferralSummary;
  referrals: ReferralListItem[];
  hasReferrals: boolean;
  pendingPayout: number;
  onWithdraw: () => void;
  onApplyToPlan: () => void;
  onShare: () => void;
}

export const BalanceHero = ({
  summary,
  referrals,
  hasReferrals,
  pendingPayout,
  onWithdraw,
  onApplyToPlan,
  onShare,
}: BalanceHeroProps) => {
  const withdrawDisabled =
    summary.availableBalance < 5000 || !summary.canWithdraw;

  return (
    <section className="balance-hero">
      <div className="glow" />
      <div className="glow-2" />
      <div className="motes">
        <span className="mote" />
        <span className="mote" />
        <span className="mote" />
        <span className="mote" />
        <span className="mote" />
      </div>

      <HeroEmblem />

      <div className="badge-live">
        <span className="pulse" />
        {hasReferrals ? 'Earning live' : 'Ready when you are'}
      </div>

      <div className="lbl">Earn wallet</div>
      <div className="num">
        {formatCurrency(summary.availableBalance)}
        <small>NGN</small>
      </div>

      {hasReferrals && referrals.length > 0 && (
        <div className="friends-stack">
          <div className="stk">
            {referrals.slice(0, 4).map((referral) => (
              <div
                key={referral.id}
                className="av"
                style={{ background: referralAvatarColor(referral.name) }}
              >
                {referralInitials(referral.name)}
              </div>
            ))}
            {referrals.length > 4 && (
              <div className="more">+{referrals.length - 4}</div>
            )}
          </div>
          <span className="lab">
            {referrals.length} friends earning with you
          </span>
        </div>
      )}

      <div className="row-stats">
        <div className="stat">
          <span className="lbl">Lifetime earned</span>
          <span className="v neon">
            {formatCurrency(summary.totalEarnings)}
          </span>
        </div>
        <div className="stat">
          <span className="lbl">Pending payout</span>
          <span className="v">{formatCurrency(pendingPayout)}</span>
        </div>
        <div className="stat">
          <span className="lbl">Reward per friend</span>
          <span className="v">Up to ₦10,500</span>
        </div>
      </div>

      <div className="actions">
        {!hasReferrals ? (
          <button type="button" className="btn-neon btn-lg" onClick={onShare}>
            <Share2 className="size-4" />
            Share my referral link
          </button>
        ) : (
          <>
            <button
              type="button"
              className="btn-neon btn-lg"
              onClick={onWithdraw}
              disabled={withdrawDisabled}
            >
              <ArrowDownRight className="size-4" />
              Cash out
            </button>
            <button
              type="button"
              className="btn-trans btn-lg"
              onClick={onApplyToPlan}
            >
              <ArrowRightLeft className="size-4" />
              Pay subscription
            </button>
            <button
              type="button"
              className="btn-trans btn-lg"
              onClick={onShare}
            >
              <Share2 className="size-4" />
              Share link
            </button>
          </>
        )}
      </div>
    </section>
  );
};
